import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader, ScanLine, Send, RotateCcw } from 'lucide-react';
import { lookupStock, submitStockCheck } from '../../supabase/services';
import NumberPad from '../common/NumberPad';
import useMediaQuery from './useMediaQuery';
import { MEDIA } from '../../utils/breakpoints';
import scanFeedback from '../../utils/scanFeedback';
import {
  CHECK_LOG_LIMIT,
  checkCorrection,
  formatSigned,
  formatUkDate,
  formatUkTime,
  loadCheckLog,
  parseQuantity,
  sanitizeQuantityText,
} from './stockTakeLogic';

const LOG_KEY = 'stockTake_checkLog';

const storageGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const storageSet = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode / quota */ }
};

const RESULT_TEXT = {
  matched: 'Matches',
  corrected: 'Corrected',
  failed: 'Failed',
  skipped: 'Not posted',
  changed: 'Stock changed',
};

// Check stock: scan a part, see what Priority says should be on the shelf, then either
// confirm it matches or type the real count. The difference is posted to Priority by
// stock-count (source 'stock-check'), which recomputes the figures itself and refuses if
// they moved since they were shown.
//
// The parent routes scans here through the ref: ref.current.scan(code, info).
const StockCheck = forwardRef(({ scanner, showToast }, ref) => {
  const [lookup, setLookup] = useState(null);       // last stock-lookup response
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [countText, setCountText] = useState('');
  const [mode, setMode] = useState('ask');          // 'ask' | 'count'
  const [confirmBig, setConfirmBig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);     // { status, message, docNo }
  const [log, setLog] = useState(() => loadCheckLog(storageGet(LOG_KEY)));
  // Touch screens get on-screen number keys: their own keyboard is hidden while a
  // Bluetooth scanner is connected, and would cover the card anyway.
  const isTouch = useMediaQuery(MEDIA.touch);
  const requestRef = useRef(0);
  const countInputRef = useRef(null);
  const cardRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { lookup, countText, mode, outcome, submitting };

  useEffect(() => {
    storageSet(LOG_KEY, JSON.stringify(log.slice(0, CHECK_LOG_LIMIT)));
  }, [log]);

  // Phones: bring the result into view after a scan (the camera or log may have left the
  // page scrolled elsewhere)
  useEffect(() => {
    if ((lookup || lookupError) && cardRef.current && typeof cardRef.current.scrollIntoView === 'function') {
      cardRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [lookup, lookupError]);

  useEffect(() => {
    if (mode === 'count' && !isTouch && countInputRef.current) countInputRef.current.focus();
  }, [mode, lookup, isTouch]);

  const runLookup = useCallback(async (code) => {
    const id = ++requestRef.current;
    setLoading(true);
    setLookupError(null);
    setOutcome(null);
    setMode('ask');
    setCountText('');
    setConfirmBig(false);
    const result = await lookupStock(code);
    if (id !== requestRef.current) return; // a newer scan is already on screen
    setLoading(false);
    if (!result.success) {
      scanFeedback('error');
      setLookup(null);
      setLookupError({ code, message: result.message || 'Could not look the part up' });
      return;
    }
    if (!result.found) {
      scanFeedback('error');
      setLookup(null);
      setLookupError({ code, message: 'Not found in Priority. Check the label or type the part number.' });
      return;
    }
    scanFeedback('success');
    setLookup(result);
  }, []);

  const scan = useCallback((code, info) => {
    // The scanner typed into the count field: put the field back, treat it as a scan
    if (info && info.fromInput && info.fromInput === countInputRef.current) {
      setCountText(sanitizeQuantityText(info.previousValue));
    }
    if (stateRef.current.submitting) {
      showToast('Still updating Priority. Scan again in a moment.', 'warning');
      return;
    }
    const { lookup: current, countText: typed, mode: currentMode, outcome: done } = stateRef.current;
    if (current && !done && currentMode === 'count' && typed !== '') {
      showToast(`Count for ${current.part.partName} was not saved`, 'warning', { duration: 6000 });
    }
    runLookup(code);
  }, [runLookup, showToast]);

  useImperativeHandle(ref, () => ({ scan }), [scan]);

  const counted = parseQuantity(countText);
  const correction = mode === 'count' ? checkCorrection(lookup, counted) : null;

  const addToLog = (entry) => setLog((prev) => [entry, ...prev].slice(0, CHECK_LOG_LIMIT));

  const submit = async (countedQty) => {
    if (!lookup || submitting) return;
    const preview = checkCorrection(lookup, countedQty);
    if (preview && preview.big && preview.difference !== 0 && !confirmBig) {
      setConfirmBig(true);
      return;
    }
    setSubmitting(true);
    const { success, result, message } = await submitStockCheck({
      barcode: lookup.part.barcode,
      partName: lookup.part.partName,
      counted: countedQty,
      expectedSeen: lookup.expected,
    });
    setSubmitting(false);
    setConfirmBig(false);

    if (result && result.status === 'changed') {
      // Something was picked / shipped / reported meanwhile: show the new figures
      scanFeedback('error');
      showToast(result.error || 'Stock changed in Priority. Check the shelf again.', 'warning', { duration: 8000 });
      setLookup((prev) => prev && ({
        ...prev,
        priorityBalance: result.priorityBalance,
        pickedNotShipped: result.pickedNotShipped,
        expected: result.expected,
      }));
      setCountText('');
      setMode('ask');
      return;
    }

    const entry = {
      at: new Date().toISOString(),
      partName: lookup.part.partName,
      description: lookup.part.description,
      expected: result ? result.expected : lookup.expected,
      counted: countedQty,
      difference: result ? result.difference : countedQty - lookup.expected,
      status: result ? result.status : 'failed',
      docNo: result ? result.docNo : null,
      error: result ? result.error : message,
    };
    addToLog(entry);

    if (success) {
      scanFeedback('success');
      setOutcome({
        status: entry.status,
        message: entry.status === 'matched'
          ? 'Matches Priority. Logged.'
          : `Priority updated ${formatSigned(entry.difference)} (${lookup.priorityBalance} → ${lookup.priorityBalance + entry.difference})`,
        docNo: entry.docNo,
      });
    } else {
      scanFeedback('error');
      setOutcome({ status: 'failed', message: entry.error || message || 'Priority was not updated', docNo: entry.docNo });
    }
  };

  const reset = () => {
    requestRef.current += 1;
    setLookup(null);
    setLookupError(null);
    setOutcome(null);
    setMode('ask');
    setCountText('');
    setConfirmBig(false);
    setLoading(false);
  };

  const renderCard = () => {
    if (loading) {
      return (
        <div className="sc-card sc-card-idle" role="status" aria-live="polite">
          <Loader className="sc-spin" size={32} aria-hidden="true" />
          <p>Looking up stock in Priority…</p>
        </div>
      );
    }
    if (lookupError) {
      return (
        <div className="sc-card sc-card-error" role="alert">
          <div className="sc-head">
            <XCircle size={28} aria-hidden="true" />
            <div>
              <div className="sc-title">{lookupError.code}</div>
              <div className="sc-sub">{lookupError.message}</div>
            </div>
          </div>
        </div>
      );
    }
    if (!lookup) {
      return (
        <div className="sc-card sc-card-idle" role="status">
          <ScanLine size={36} aria-hidden="true" />
          <p><strong>Scan a product</strong> to see the stock Priority holds for it.</p>
          <p className="sc-sub sc-idle-more">Then confirm it matches the shelf, or type the real count and Priority is corrected by the difference.</p>
        </div>
      );
    }

    const { part, locations, pickedNotShipped, priorityBalance, expected, lastCheck, elsewhere } = lookup;
    return (
      <div className={`sc-card ${outcome ? `sc-card-${outcome.status === 'failed' ? 'error' : 'done'}` : ''}`.trim()}>
        <div className="sc-head">
          <div className="sc-head-text">
            <div className="sc-title">{part.description || part.partName}</div>
            <div className="sc-sub"><span className="sc-mono">{part.partName}</span>{part.barcode && <> · <span className="sc-mono">{part.barcode}</span></>}</div>
          </div>
        </div>

        <div className="sc-figures">
          <div className="sc-fig">
            <span className="sc-fig-label">Priority (FG)</span>
            <span className="sc-fig-value">{priorityBalance}</span>
          </div>
          <div className="sc-fig">
            <span className="sc-fig-label">Picked, not shipped</span>
            <span className="sc-fig-value">{pickedNotShipped ? `−${pickedNotShipped}` : '0'}</span>
          </div>
          <div className="sc-fig sc-fig-main">
            <span className="sc-fig-label">Should be on shelf</span>
            <span className="sc-fig-value" data-testid="sc-expected">{expected}</span>
          </div>
        </div>

        <div className="sc-meta">
          {locations.length > 0
            ? locations.map((l) => <span key={l.location}>FG / {l.location}: <strong>{l.balance}</strong></span>)
            : <span>No stock in FG</span>}
          {(elsewhere || []).map((l) => (
            <span key={`${l.warehouse}-${l.location}-${l.status}`} className="sc-elsewhere">{l.warehouse} / {l.location} ({l.status}): {l.balance}</span>
          ))}
          {lastCheck && (
            <span className="sc-last">Last checked {formatUkDate(lastCheck.created_at)} {formatUkTime(lastCheck.created_at)}: {lastCheck.counted}{lastCheck.difference ? ` (${formatSigned(Number(lastCheck.difference))})` : ''}{lastCheck.kind ? `, ${lastCheck.kind}` : ''}</span>
          )}
        </div>

        {outcome ? (
          <div className={`sc-outcome sc-outcome-${outcome.status === 'failed' ? 'error' : 'ok'}`} role="status">
            {outcome.status === 'failed' ? <XCircle size={22} aria-hidden="true" /> : <CheckCircle2 size={22} aria-hidden="true" />}
            <div>
              <strong>{outcome.message}</strong>
              {outcome.docNo && <div className="sc-sub">Count document {outcome.docNo}</div>}
              <div className="sc-sub">Scan the next product.</div>
            </div>
          </div>
        ) : lookup.tracked === false ? (
          <div className="sc-outcome sc-outcome-error" role="alert">
            <AlertTriangle size={22} aria-hidden="true" />
            <div>
              <strong>Priority doesn't keep stock for this part.</strong> "Inventory Part" is switched off on it in
              Priority, so a count can't change its stock. Ask the office to switch it on, then count it again.
            </div>
          </div>
        ) : !lookup.canCorrect ? (
          <div className="sc-outcome sc-outcome-error" role="alert">
            <AlertTriangle size={22} aria-hidden="true" />
            <div><strong>This part has no barcode in Priority</strong>, so it can't be corrected from here.</div>
          </div>
        ) : mode === 'ask' ? (
          <div className="sc-actions">
            <button type="button" className="st-btn sc-btn-match" onClick={() => submit(Math.max(0, expected))} disabled={submitting || expected < 0}>
              <CheckCircle2 size={20} aria-hidden="true" /> Matches shelf ({expected})
            </button>
            <button type="button" className="st-btn st-btn-secondary" onClick={() => setMode('count')} disabled={submitting}>
              Doesn't match, enter count
            </button>
          </div>
        ) : (
          <div className="sc-count">
            <label htmlFor="sc-count-input" className="sc-fig-label">Real count on the shelf</label>
            <div className="sc-count-row">
              <input
                id="sc-count-input"
                ref={countInputRef}
                className="sc-count-input"
                type="text"
                inputMode={isTouch ? 'none' : 'numeric'}
                pattern="[0-9]*"
                autoComplete="off"
                data-scan-capture="true"
                value={countText}
                onChange={(e) => { setCountText(sanitizeQuantityText(e.target.value)); setConfirmBig(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && counted !== null) submit(counted); }}
                placeholder="0"
                disabled={submitting}
              />
              <button
                type="button"
                className={`st-btn ${confirmBig ? 'st-btn-warning' : 'st-btn-primary'}`}
                onClick={() => submit(counted)}
                disabled={submitting || counted === null}
              >
                {submitting ? <><Loader className="sc-spin" size={18} aria-hidden="true" /> Updating…</>
                  : confirmBig ? <>Yes, update {formatSigned(correction?.difference)}</>
                  : <><Send size={18} aria-hidden="true" /> Update Priority</>}
              </button>
            </div>
            {isTouch && (
              <NumberPad
                value={countText}
                onChange={(text) => { setCountText(sanitizeQuantityText(text)); setConfirmBig(false); }}
                disabled={submitting}
                label="Real count"
              />
            )}
            {correction && (
              <p className={`sc-calc ${correction.difference === 0 ? 'is-zero' : correction.difference > 0 ? 'is-over' : 'is-short'}`} aria-live="polite">
                {correction.difference === 0
                  ? 'Same as Priority: nothing to correct, it will just be logged.'
                  : <>Difference <strong>{formatSigned(correction.difference)}</strong>: Priority goes from {lookup.priorityBalance} to <strong>{correction.newBalance}</strong>.</>}
                {confirmBig && <span className="sc-warn"> That's a big difference. Count again if unsure, then confirm.</span>}
              </p>
            )}
            <button type="button" className="sc-link" onClick={() => { setMode('ask'); setCountText(''); setConfirmBig(false); }} disabled={submitting}>
              Back
            </button>
          </div>
        )}
      </div>
    );
  };

  const corrections = log.filter((r) => r.status === 'corrected');

  return (
    <>
      <div className="stock-take-scanner st-scan-area sc-scan-area">
        {scanner}
        <div ref={cardRef} className="sc-card-wrap">{renderCard()}</div>
      </div>

      <div className="stock-take-summary sc-summary">
        <span className="summary-label">Checked today<span className="st-hide-phone"> on this device</span>:</span>
        <span className="summary-value">
          {log.length} part{log.length !== 1 ? 's' : ''}, {corrections.length} corrected
        </span>
        {lookup && (
          <button type="button" className="sc-link sc-clear" onClick={reset}>
            <RotateCcw size={14} aria-hidden="true" /> Clear
          </button>
        )}
      </div>

      {log.length > 0 && (
        <div className="scanned-items-wrapper">
          <table className="scanned-items-table sc-log-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Product</th>
                <th>Should be</th>
                <th>Counted</th>
                <th>Difference</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {log.map((r) => (
                <tr key={`${r.at}-${r.partName}`} className={`sc-row-${r.status}`}>
                  <td className="time-cell sc-c-time">{formatUkTime(r.at)}</td>
                  <td className="sc-c-prod">
                    <div className="sc-row-desc">{r.description || r.partName}</div>
                    <div className="sc-mono sc-sub">{r.partName}<span className="sc-show-phone"> · {formatUkTime(r.at)}</span></div>
                  </td>
                  <td className="sc-c-num" data-label="Should be">{r.expected}</td>
                  <td className="sc-c-num" data-label="Counted">{r.counted}</td>
                  <td className={`sc-c-num ${r.difference > 0 ? 'is-over' : r.difference < 0 ? 'is-short' : ''}`} data-label="Difference">{formatSigned(r.difference)}</td>
                  <td className="sc-c-res">
                    <span className={`sc-badge sc-badge-${r.status}`}>{RESULT_TEXT[r.status] || r.status}</span>
                    {r.docNo && <div className="sc-mono sc-sub">{r.docNo}</div>}
                    {r.error && r.status === 'failed' && <div className="sc-sub sc-err">{r.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
});

StockCheck.displayName = 'StockCheck';

export default StockCheck;
